import { type ReactElement } from 'react';
import { useTranslation } from 'react-i18next';
import Slider from 'rc-slider';

import { formatUtcDayShort } from './runAnalysisSelectors';

import type { TimelineRange } from './runAnalysisSelectors';

import 'rc-slider/assets/index.css';

import styles from './scss/TargetedMarketing.module.scss';

interface IWatchTowerIssueTimeline {
  days: string[],
  range: TimelineRange,
  markCounts: Record<string, number>,
  onRangeChange: (range: TimelineRange) => void,
  onMarkClick: (day: string) => void,
}

function markLeftPercent(index: number, total: number): string {
  if (total <= 1) {
    return '0%';
  }
  return `${(index / (total - 1)) * 100}%`;
}

function markClassName(selected: boolean, inRange: boolean): string {
  if (selected) {
    return `${styles.timelineMark} ${styles.timelineMarkActive}`;
  }
  if (inRange) {
    return styles.timelineMark;
  }
  return `${styles.timelineMark} ${styles.timelineMarkMuted}`;
}

function isRangeValue(value: number | number[]): value is number[] {
  return Array.isArray(value) && value.length >= 2;
}

function WatchTowerIssueTimeline({
  days,
  range,
  markCounts,
  onRangeChange,
  onMarkClick,
}: IWatchTowerIssueTimeline): ReactElement {
  const { t } = useTranslation();
  const startDay = days[range[0]] || '';
  const endDay = days[range[1]] || '';
  const lastIndex = Math.max(days.length - 1, 0);

  const onSliderChange = (value: number | number[]) => {
    if (!isRangeValue(value)) {
      return;
    }
    onRangeChange([value[0], value[1]]);
  };

  const dayLabel = (index: number): string => days[index] || '';

  if (days.length === 0) {
    return <p className={styles.muted}>{t('targetedMarketing.emptyTimeline')}</p>;
  }

  return (
    <div className={styles.timeline}>
      <p className={styles.timelineRange}>
        {t('targetedMarketing.timelineRange', { start: startDay, end: endDay })}
      </p>
      <div className={styles.timelineAxis}>
        <div className={styles.timelineMarks}>
          {days.map((day, index) => {
            const count = markCounts[day] || 0;
            if (!count) {
              return null;
            }
            const inRange = index >= range[0] && index <= range[1];
            const selected = range[0] === index && range[1] === index;
            return (
              <button
                key={day}
                type="button"
                className={markClassName(selected, inRange)}
                style={{ left: markLeftPercent(index, days.length) }}
                aria-label={t('targetedMarketing.timelineMark', { day, count })}
                title={`${day} (${count})`}
                onClick={() => onMarkClick(day)}
              />
            );
          })}
        </div>
        <div className={styles.timelineSlider}>
          <Slider
            className={styles.timelineRcSlider}
            range={{ draggableTrack: true }}
            min={0}
            max={lastIndex}
            step={1}
            allowCross={false}
            value={[range[0], range[1]]}
            ariaLabelForHandle={[
              t('targetedMarketing.timelineStart'),
              t('targetedMarketing.timelineEnd'),
            ]}
            ariaValueTextFormatterForHandle={[dayLabel, dayLabel]}
            onChange={onSliderChange}
          />
        </div>
      </div>
      <div className={styles.timelineLabels}>
        <span>{formatUtcDayShort(days[0])}</span>
        <span>
          {t('targetedMarketing.timelineToday')}
          {' · '}
          {formatUtcDayShort(days[lastIndex])}
        </span>
      </div>
    </div>
  );
}

export default WatchTowerIssueTimeline;
